import React, { useState, useEffect } from "react";
import { Field, FieldArray, useFormikContext, ErrorMessage } from "formik";
import SubServiceModal from "./service-model";
import {
  ServiceFormValues,
  SubService,
  Merchandise,
} from "../service-management-type";
import { SelectInputComponent } from "../../../../../common/components";
import AddCategoryModal from "./add-new-category-popup";
import InfoIcon from "../../../../../assets/icons/info-icon.svg";
import { AntSwitch } from "../../../../../common/components/ant-switch/ant-switch";
import PlusIcon from "../../../../../assets/icons/plus-icon.svg";
import { KeyboardArrowDown } from "@mui/icons-material";
import NoSubServicesFallBackIcon from "../../../../../assets/icons/no-sub-services-fallback-icon.svg";
import CommonListMUI from "./service-list-table";
import { subServiceHeaders, merchandiseHeaders } from "./create-service-helper";
import MerchandiseModal from "./merchandise-modal";
import {
  getCategories,
  createNewServiceCategory,
} from "../../../../../api/admin/service-management";
import CommissionSelect from "./commission-select";

const PERCENTAGE_TYPE = "commissionPercentType";
const VALUE_TYPE = "commissionValueType";

const commissionTypeOption = [
  { value: PERCENTAGE_TYPE, label: "Commission(%)" },
  { value: VALUE_TYPE, label: "Commission($)" },
];
interface ServiceFormProps {
  isReadOnly?: boolean;
  setCategoryIds?: React.Dispatch<React.SetStateAction<number[]>>;
  setLoading?: React.Dispatch<React.SetStateAction<boolean>>;
  setAlertOpen?: React.Dispatch<React.SetStateAction<boolean>>;
  setAlertType?: React.Dispatch<
    React.SetStateAction<"error" | "success" | "info" | "warning">
  >;
  setAlertMessage?: React.Dispatch<React.SetStateAction<string | null>>;
  isEdit?: boolean;
}

const ServiceForm: React.FC<ServiceFormProps> = ({
  isReadOnly,
  setCategoryIds,
  setLoading,
  setAlertOpen,
  setAlertType,
  setAlertMessage,
  isEdit,
}) => {
  const { values, setFieldValue } = useFormikContext<ServiceFormValues>();

  const [openSubService, setOpenSubService] = useState(false);
  const [isAddNewCategoryOpen, setIsAddNewCategoryOpen] = useState(false);
  const [isSubServicesExpanded, setIsSubServicesExpanded] = useState(false);
  const [editingSubService, setEditingSubService] = useState<{
    index: number;
    subService: SubService;
  } | null>(null);
  const [isMerchandiseExpanded, setIsMerchandiseExpanded] = useState(false);
  const [openMerchandise, setOpenMerchandise] = useState(false);
  const [editingMerchandise, setEditingMerchandise] = useState<{
    index: number;
    merchandise: Merchandise;
  } | null>(null);
  const [availableCategories, setAvailableCategories] = useState<
    Array<{ value: string; label: string; id: number }>
  >([]);

  useEffect(() => {
    if (!isEdit && values.name) {
      const generatedCode = `MEM-${values.name.replace(/\s+/g, "").toUpperCase()}`;
      setFieldValue("code", generatedCode);
    }
  }, [values.name, isEdit, setFieldValue]);
  const fetchCategories = async () => {
    try {
      const response = await getCategories({ type: "all" });
      if (response.status === 200) {
        const categoriesData = response?.data?.data?.data || [];
        const formattedCategories = categoriesData?.map((category: any) => ({
          value: category?.name,
          label: category?.name,
          id: category?.id,
        }));
        setAvailableCategories(formattedCategories);
      }
    } catch (error) {
      console.error("Error fetching categories:", error);
    }
  };

  const createCategoryApi = async (categoryName: string) => {
    setLoading?.(true);
    try {
      const response = await createNewServiceCategory({ name: categoryName });
      if (response?.data?.success === true) {
        // Optionally, you can refresh the categories list here
        setCategoryIds?.((prevIds) => [...prevIds, response?.data?.data?.id]);
        setIsAddNewCategoryOpen(false);
        setAlertMessage?.("Category created successfully.");
        setAlertType?.("success");
        setAlertOpen?.(true);
        const updatedCategories = values.category
          ? [...values.category, categoryName.trim()]
          : [categoryName.trim()];
        setFieldValue("category", updatedCategories);
        fetchCategories();
      }
    } catch (error: any) {
      console.error("Error creating category:", error?.response?.data);
      setAlertMessage?.(
        error?.response?.data?.message || "Failed to create category.",
      );
      setAlertType?.("error");
      setAlertOpen?.(true);
    } finally {
      setLoading?.(false);
    }
  };

  const handleAddCategory = async (newCategory: string) => {
    await createCategoryApi(newCategory);
    // setCategoryIds?.((prevIds) => [...prevIds, newCategory]);
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  const ErrorText = ({ name }: { name: string }) => (
    <ErrorMessage name={name} component="div" className="error-message" />
  );

  const calculateServiceBaseMinPrice = () => {
    if (values.subServices.length === 0) return 0;
    return values.subServices.reduce(
      (total, subService) => total + subService.minPrice,
      0,
    );
  };

  const calculateServiceBaseMaxPrice = () => {
    if (values.subServices.length === 0) return 0;
    return values.subServices.reduce(
      (total, subService) => total + subService.maxPrice,
      0,
    );
  };

  //For now no need to calculate from admin side

  useEffect(() => {
    if(values?.commissionType){
      return;
    }
    if (values.commissionValue) {
      setFieldValue("commissionType", VALUE_TYPE);
    } else{
      setFieldValue("commissionType", PERCENTAGE_TYPE);
    }
  }, [values]);

  const getCommissionDetails = () => {
    return (
      <>
        <div className="row">
          <div className="commission-label-and-value">
            <label>Commission Type</label>
            <CommissionSelect
              value={values.commissionType === PERCENTAGE_TYPE ? PERCENTAGE_TYPE : VALUE_TYPE}
              handleChange={(e: any) => {
                setFieldValue("commissionType", e.target.value);
              }}
              placeholder="Select Commission Type"
              options={commissionTypeOption}
            />
          </div>

          <div className="commission-label-and-value">
            <label>
              Commission{" "}
              {values.commissionType === PERCENTAGE_TYPE
                ? "Percent"
                : "Value"}
            </label>
            {values.commissionType === VALUE_TYPE && (
              <>
                <Field name="commissionValue">
                  {({ field, form }: any) => (
                    <input
                      {...field}
                      type="text"
                      disabled={!values.commissionType}
                      placeholder="value"
                      min={0}
                      onChange={(e) => {
                        let targetValue = e.target.value;
                        if (targetValue === "") {
                          form.setFieldValue("commissionValue", "");
                          return;
                        }

                        // Allow only digits
                        if (!/^\d*$/.test(targetValue)) {
                          return;
                        }
                        targetValue = targetValue.replace(/\D/g, "");
                        const value = Number(
                          targetValue.replace(/^0+(?=\d)/, ""),
                        );
                        const max = 999999;

                        if (value <= max) {
                          form.setFieldValue("commissionValue", value);
                          form.setFieldValue("commissionPercent", 0);
                        }
                      }}
                    />
                  )}
                </Field>

                <ErrorText name="commissionValue" />
              </>
            )}
            {values.commissionType === PERCENTAGE_TYPE && (
              <>
                <Field name="commissionPercent">
                  {({ field, form }: any) => (
                    <input
                      {...field}
                      type="text"
                      disabled={!values.commissionType}
                      placeholder="value"
                      min={0}
                      onChange={(e) => {
                        let targetValue = e.target.value;
                        if (targetValue === "") {
                          form.setFieldValue("commissionPercent", "");
                          return;
                        }

                        if (!/^\d*$/.test(targetValue)) {
                          return;
                        }
                        targetValue = targetValue.replace(/\D/g, "");
                        const value = Number(
                          targetValue.replace(/^0+(?=\d)/, ""),
                        );
                        const max = 100;
                        if (value <= max) {
                          form.setFieldValue("commissionPercent", value);
                          form.setFieldValue("commissionValue", 0);
                        }
                      }}
                    />
                  )}
                </Field>

                <ErrorText name="commissionPercent" />
              </>
            )}
          </div>
        </div>
      </>
    );
  };
  return (
    <div className="service-form-card">
      <h3>Service Details</h3>

      {/* Service Name */}
      <label>
        Service Name <span className="required">*</span>
      </label>
      <Field
        name="name"
        placeholder="Enter service name"
        maxLength={50}
        disabled={isReadOnly}
      />
      <ErrorText name="name" />

      {/* Service Code */}
      <label>
        Service Code / Unique Identifier <span className="required">*</span>
      </label>
      <Field name="code" placeholder="Enter service code" disabled />
      <ErrorText name="code" />

      {/* Description */}
      <label>Service Description</label>
      <Field
        name="description"
        as="textarea"
        placeholder="Enter service description"
        maxLength={300}
        disabled={isReadOnly}
      />
      <ErrorText name="description" />

      {/* Category */}
      <div className="category-label-with-add-category">
        <label>
          Category <span className="required">*</span>
        </label>
        <button
          type="button"
          className="add-new-category-button"
          onClick={() =>
            !isReadOnly && setIsAddNewCategoryOpen(!isAddNewCategoryOpen)
          }

          // we will allow this next sprint as we need other fields like needTYpe,image and desc also in next sprint
          // disabled={true}
        >
          + Add New Category
        </button>
      </div>

      <SelectInputComponent
        fieldName="category"
        options={availableCategories}
        placeholder={
          isAddNewCategoryOpen ? "Enter new category" : "Select category"
        }
        value={values.category || []}
        onChange={(val: any) => {
          !isReadOnly && setFieldValue("category", val);
          //find ids of selected categories and set in parent component
          const selectedCategoryIds = val.map((category: string) => {
            //find id from availableCategories
            const foundCategory = availableCategories.find(
              (cat) => cat.value === category,
            );
            return foundCategory ? foundCategory.id : null;
          });
          //consider previous ids as well and set unique ids only
          const previousIds = selectedCategoryIds;
          const allIds = [...previousIds, ...selectedCategoryIds];
          const uniqueIds = Array.from(new Set(allIds));
          setCategoryIds?.(uniqueIds as number[]);
        }}
        notEditable={!!isReadOnly}
        multiple={true}
      />
      <ErrorText name="category" />

      {isAddNewCategoryOpen && (
        <AddCategoryModal
          onClose={() => setIsAddNewCategoryOpen(false)}
          isReadOnly={!!isReadOnly}
          onCreated={() => {
            fetchCategories(); // ✅ refetch latest categories
          }}
          // handleAddCategory={handleAddCategory}
        />
      )}

      {/* Pricing */}
      {/* <h4 className="pricing-heading">Pricing</h4> */}

      <label className="service-heading">Service Base Price ($)</label>
      <div className="row">
        <div className="price-field">
          <Field
            name="minPrice"
            type="number"
            placeholder="$ Min price"
            disabled={isReadOnly}
            onWheel={(e) =>
              e.target instanceof HTMLElement && e.target.blur()
            }
          />
          <ErrorText name="minPrice" />
        </div>

        <div className="price-field">
          <Field
            name="maxPrice"
            type="number"
            placeholder="$ Max price"
            disabled={isReadOnly}
             onWheel={(e) =>
              e.target instanceof HTMLElement && e.target.blur()
            }
          />
          <ErrorText name="maxPrice" />
        </div>
      </div>

      <div className="pricing-notes">
        <img src={InfoIcon} alt="Info" className="info-icon" />
        Note: Min and Max prices define the allowable price range for this
        service. Providers can select or price their services only within this
        range.
      </div>

      {/* Sub Services */}
      <div className="sub-services-section">
        <div
          className="sub-services-header"
          onClick={() => setIsSubServicesExpanded(!isSubServicesExpanded)}
        >
          <div className="header-content">
            <div className="left-section">
              <img src={PlusIcon} alt="Sub-Services" className="plus-icon" />
              <div className="heading-text">
                <h4>Add or Manage Sub-Services</h4>
                <span className="sub-text">
                  {" "}
                  Click to add sub-services to this service{" "}
                </span>
              </div>
            </div>
            <KeyboardArrowDown
              className={`arrow-icon ${isSubServicesExpanded ? "expanded" : ""}`}
            />
          </div>
        </div>

        {isSubServicesExpanded && (
          <FieldArray name="subServices">
            {({ push, remove, replace }) => (
              <div className="sub-services-content">
                <div className="header-text-and-button">
                  <div className="text-section">
                    <p className="sub-services-heading">Sub-Services List</p>
                    <p className="sub-services-description">
                      {" "}
                      Add and manage sub-services for detailed pricing
                      breakdown{" "}
                    </p>
                  </div>
                  <button
                    type="button"
                    className="add-sub-service-btn"
                    onClick={() => !isReadOnly && setOpenSubService(true)}
                    disabled={!!isReadOnly}
                  >
                    <img src={PlusIcon} alt="Add" /> Add Sub-Service
                  </button>
                </div>
                {values.subServices.length > 0 ? (
                  <CommonListMUI
                    headers={subServiceHeaders}
                    data={values.subServices}
                    onEdit={
                      isReadOnly
                        ? undefined
                        : (rowData) => {
                            // Use ID to find the item instead of array index comparison
                            const index = rowData.id
                              ? values.subServices.findIndex(
                                  (ss) => ss.id === rowData.id,
                                )
                              : values.subServices.findIndex(
                                  (ss) => ss === rowData,
                                );
                            setEditingSubService({
                              index,
                              subService: rowData,
                            });
                          }
                    }
                    onDelete={
                      isReadOnly
                        ? undefined
                        : (rowData) => {
                            // Remove object from array based on ID or object reference
                            const index = rowData.id
                              ? values.subServices.findIndex(
                                  (ss) => ss.id === rowData.id,
                                )
                              : values.subServices.findIndex(
                                  (ss) => ss === rowData,
                                );
                            remove(index);
                          }
                    }
                    isReadOnly={isReadOnly}
                  />
                ) : (
                  <div className="no-sub-services-wrapper">
                    <img
                      src={NoSubServicesFallBackIcon}
                      alt="No Sub-Services"
                      className="no-sub-services-fallback-icon"
                    />
                    <p className="no-sub-services-text">
                      {" "}
                      No sub-services added yet{" "}
                    </p>
                    <p className="no-sub-services-text">
                      {" "}
                      {`Click "Add Sub-Service" to get started`}{" "}
                    </p>
                  </div>
                )}

                {openSubService && (
                  <SubServiceModal
                    onClose={() => setOpenSubService(false)}
                    onSave={(subService) => {
                      push(subService);
                      setOpenSubService(false);
                    }}
                    isReadOnly={!!isReadOnly}
                  />
                )}

                {editingSubService && (
                  <SubServiceModal
                    subService={editingSubService.subService}
                    onClose={() => setEditingSubService(null)}
                    onSave={(updated) => {
                      replace(editingSubService.index, updated);
                      setEditingSubService(null);
                    }}
                  />
                )}
              </div>
            )}
          </FieldArray>
        )}
      </div>

      {/* Merchandise */}
      <div className="sub-services-section">
        <div
          className="sub-services-header"
          onClick={() => setIsMerchandiseExpanded(!isMerchandiseExpanded)}
        >
          <div className="header-content">
            <div className="left-section">
              <img src={PlusIcon} alt="Merchandise" className="plus-icon" />
              <div className="heading-text">
                <h4>Add or Manage Merchandise</h4>
                <span className="sub-text">
                  {" "}
                  Click to add merchandise to this service{" "}
                </span>
              </div>
            </div>
            <KeyboardArrowDown
              className={`arrow-icon ${isMerchandiseExpanded ? "expanded" : ""}`}
            />
          </div>
        </div>

        {isMerchandiseExpanded && (
          <FieldArray name="merchandises">
            {({ push, remove, replace }) => (
              <div className="sub-services-content">
                <div className="header-text-and-button">
                  <div className="text-section">
                    <p className="sub-services-heading">Merchandise List</p>
                    <p className="sub-services-description">
                      {" "}
                      Add and manage merchandise for detailed pricing
                      breakdown{" "}
                    </p>
                  </div>
                  <button
                    type="button"
                    className="add-sub-service-btn"
                    onClick={() => !isReadOnly && setOpenMerchandise(true)}
                    disabled={!!isReadOnly}
                  >
                    <img src={PlusIcon} alt="Add" /> Add Merchandise
                  </button>
                </div>
                {values.merchandises.length > 0 ? (
                  <CommonListMUI
                    headers={merchandiseHeaders}
                    data={values.merchandises}
                    onEdit={
                      isReadOnly
                        ? undefined
                        : (rowData) => {
                            // Use ID to find the item instead of array index comparison
                            const index = rowData.id
                              ? values.merchandises.findIndex(
                                  (m) => m.id === rowData.id,
                                )
                              : values.merchandises.findIndex(
                                  (m) => m === rowData,
                                );
                            setEditingMerchandise({
                              index,
                              merchandise: rowData,
                            });
                          }
                    }
                    onDelete={
                      isReadOnly
                        ? undefined
                        : (rowData) => {
                            // Remove object from array based on ID or object reference
                            const index = rowData.id
                              ? values.merchandises.findIndex(
                                  (m) => m.id === rowData.id,
                                )
                              : values.merchandises.findIndex(
                                  (m) => m === rowData,
                                );
                            remove(index);
                          }
                    }
                    isReadOnly={isReadOnly}
                  />
                ) : (
                  <div className="no-sub-services-wrapper">
                    <img
                      src={NoSubServicesFallBackIcon}
                      alt="No Sub-Services"
                      className="no-sub-services-fallback-icon"
                    />
                    <p className="no-sub-services-text">
                      {" "}
                      No merchandise added yet{" "}
                    </p>
                    <p className="no-sub-services-text">
                      {" "}
                      {`Click "Add Merchandise" to get started`}{" "}
                    </p>
                  </div>
                )}

                {openMerchandise && (
                  <MerchandiseModal
                    onClose={() => setOpenMerchandise(false)}
                    onSave={(merchandise) => {
                      push(merchandise);
                      setOpenMerchandise(false);
                    }}
                    isReadOnly={!!isReadOnly}
                  />
                )}

                {editingMerchandise && (
                  <MerchandiseModal
                    merchandise={editingMerchandise.merchandise}
                    onClose={() => setEditingMerchandise(null)}
                    onSave={(updated) => {
                      replace(editingMerchandise.index, updated);
                      setEditingMerchandise(null);
                    }}
                  />
                )}
              </div>
            )}
          </FieldArray>
        )}
      </div>
      {/* Commission */}
      <h4 className="commission-heading">Commission</h4>

      {getCommissionDetails()}

      {/* Status */}
      <div className="status-wrapper">
        <div className="toggle-row">
          <span className="status-label">Status</span>
          <AntSwitch
            checked={values.status}
            onChange={(e, checked) => setFieldValue("status", checked)}
            disabled={!!isReadOnly}
          />
        </div>
        <span className="notes">Set service as active or inactive</span>
      </div>
      {/* Tax Applicable wrapper */}
      <div className="tax-applicable-wrapper">
        <div className="toggle-row">
          <span className="tax-label">Tax Applicability (Coming Soon)</span>
          <AntSwitch
            // checked={values.taxApplicable}
            disabled={true}
            checked={false}
          />
        </div>
      </div>
    </div>
  );
};

export default ServiceForm;
